import { type Card, cardId, sameCard } from '../trick/cards.js';
import { wrapFromOf } from './play.js';
import { bestMeldPlan, cardValue, layOffOnto, rankGap, ringStep, wraps } from './melds.js';
import type { RummyView } from './view.js';

/**
 * `easy` draws from the stock (sometimes taking a discard that completes a meld), lays down a meld when it sees one, never lays cards off
 * and discards its highest card that has no partner in its hand.
 * `clever` takes a discard that helps, lays down melds and lays cards off whenever it can, and discards the card it needs least:
 * high value, no partner in its hand that could still be completed from the cards nobody has seen.
 */
export type RummyRobotLevel = 'easy' | 'clever';

export type RummyAction =
  | { readonly type: 'draw'; readonly from: 'stock' | 'discard' }
  | { readonly type: 'meld'; readonly cards: readonly Card[] }
  | { readonly type: 'layoff'; readonly card: Card; readonly meldId: number }
  | { readonly type: 'discard'; readonly card: Card };

/** The first card of the hand that fits some meld on the table, with that meld, or null. Highest value first so the most points leave the hand. */
function findLayOff(view: RummyView): { card: Card; meldId: number } | null {
  const cards = [...view.hand].sort((a, b) => cardValue(b, view.aceHigh) - cardValue(a, view.aceHigh));
  for (const card of cards) {
    for (const meld of view.melds) if (layOffOnto(meld, card, view.aceHigh, wrapFromOf(view))) return { card, meldId: meld.id };
  }
  return null;
}

/** How much a card is worth keeping: partners in the hand (same rank, or same suit within two ranks) that can still be completed. */
function keepValue(card: Card, view: RummyView, seen: ReadonlySet<string>): number {
  const inHand = new Set(view.hand.map(cardId));
  const free = (c: Card) => !inHand.has(cardId(c)) && !seen.has(cardId(c));
  let keep = 0;
  const sameRank = view.hand.filter((c) => c !== card && c.rank === card.rank);
  if (sameRank.length > 0) {
    const missing = (['C', 'D', 'H', 'S'] as const).filter((s) => !sameRank.some((c) => c.suit === s) && s !== card.suit);
    if (missing.some((s) => free({ suit: s, rank: card.rank }))) keep += 6 * sameRank.length;
  }
  for (const other of view.hand) {
    if (other === card || other.suit !== card.suit) continue;
    const gap = rankGap(other.rank, card.rank, wrapFromOf(view));
    if (gap === 0 || gap > 2) continue;
    // `a` then `b` are the two cards in run order. On the Big Cards ring "below" and "above" wrap around.
    const ring = wraps(wrapFromOf(view));
    const a = ring ? (ringStep(card.rank, gap, view.minRank) === other.rank ? card.rank : other.rank) : Math.min(other.rank, card.rank);
    const b = ring ? ringStep(a, gap, view.minRank) : Math.max(other.rank, card.rank);
    const wanted = gap === 1 ? [a - 1, b + 1] : [a + 1];
    const valid = (r: number) => (ring ? ringStep(r, 0, view.minRank) : r >= 2 && r <= 14 ? r : 0);
    if (wanted.some((r) => valid(r) !== 0 && free({ suit: card.suit, rank: valid(r) as Card['rank'] }))) keep += 5;
  }
  return keep;
}

function cleverDiscard(view: RummyView): Card {
  const seen = new Set([...view.discard, ...view.melds.flatMap((m) => m.cards)].map(cardId));
  let best = view.discardable[0] as Card;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (const card of view.discardable) {
    const score = cardValue(card, view.aceHigh) - keepValue(card, view, seen);
    if (score > bestScore || (score === bestScore && card.rank > best.rank)) {
      best = card;
      bestScore = score;
    }
  }
  return best;
}

/** Does the card have a partner in the hand: another of its rank, or a card of its suit one or two ranks away? */
const hasPartner = (card: Card, hand: readonly Card[], wrapFrom?: Card['rank']): boolean =>
  hand.some((o) => o !== card && (o.rank === card.rank || (o.suit === card.suit && rankGap(o.rank, card.rank, wrapFrom) <= 2)));

/** The highest card with no partner in the hand (a child keeps a pair); when every card has one, the highest card. */
function easyDiscard(view: RummyView, random: () => number): Card {
  const lonely = view.discardable.filter((c) => !hasPartner(c, view.hand, wrapFromOf(view)));
  const pool = lonely.length > 0 ? lonely : view.discardable;
  const top = Math.max(...pool.map((c) => cardValue(c, view.aceHigh)));
  const highest = pool.filter((c) => cardValue(c, view.aceHigh) === top);
  return highest[Math.floor(random() * highest.length)] as Card;
}

/**
 * What a robot does on its turn: it sees only its own `RummyView`. `random` is used by the easy robot only, so a clever game is fully
 * deterministic. One action per call; the caller asks again until the turn passes.
 */
export function rummyChoose(view: RummyView, level: RummyRobotLevel, random: () => number): RummyAction {
  if (!view.canAct) throw new Error("rummyChoose: not this seat's turn");
  if (view.phase === 'draw') {
    if (!view.canDrawStock) return { type: 'draw', from: 'discard' };
    const top = view.top;
    if (top && view.canDrawDiscard) {
      const now = bestMeldPlan(view.hand, view.aceHigh, wrapFromOf(view)).meldedValue;
      const withTop = bestMeldPlan([...view.hand, top], view.aceHigh, wrapFromOf(view)).meldedValue;
      const fitsTable = view.melds.some((m) => layOffOnto(m, top, view.aceHigh, wrapFromOf(view)));
      // Easy only notices a discard that completes a meld, and then only about half the time; clever also sees a card that fits the table.
      if (level === 'easy' ? withTop > now && random() < 0.5 : withTop > now || fitsTable) return { type: 'draw', from: 'discard' };
    }
    return { type: 'draw', from: 'stock' };
  }
  // The play phase: melds first, then (clever only) lay-offs, then the discard that ends the turn.
  const plan = bestMeldPlan(view.hand, view.aceHigh, wrapFromOf(view));
  const meld = plan.melds[0];
  if (meld) return { type: 'meld', cards: meld.cards };
  if (level === 'clever') {
    const off = findLayOff(view);
    if (off) return { type: 'layoff', card: off.card, meldId: off.meldId };
    return { type: 'discard', card: cleverDiscard(view) };
  }
  return { type: 'discard', card: easyDiscard(view, random) };
}

/**
 * A suggestion for a human: the clever robot's choice for this view (take the discard, lay down this meld, lay this card off, discard that
 * one). Reads only the viewer's own view, so it can never leak a hidden card.
 */
export const rummyHint = (view: RummyView): RummyAction => rummyChoose(view, 'clever', () => 0);

export const sameAction = (a: RummyAction, b: RummyAction): boolean => {
  if (a.type !== b.type) return false;
  if (a.type === 'draw' && b.type === 'draw') return a.from === b.from;
  if (a.type === 'discard' && b.type === 'discard') return sameCard(a.card, b.card);
  if (a.type === 'layoff' && b.type === 'layoff') return sameCard(a.card, b.card) && a.meldId === b.meldId;
  if (a.type === 'meld' && b.type === 'meld') return a.cards.length === b.cards.length && a.cards.every((c) => b.cards.some((d) => sameCard(c, d)));
  return false;
};
